import React from 'react';

/**
 * "Sign in with Apple" button, drawn to Apple's Human Interface Guidelines.
 *
 * Apple's own JS SDK can render a button, but only the div-attribute variant,
 * which fires window events instead of resolving the `signIn()` promise the
 * popup flow gives us. Drawing it here keeps ONE code path (click ->
 * signInWithApple -> token) and lets the button match the Google button's
 * geometry so the two do not look like they came from different products.
 *
 * The constraints below are Apple's brand requirements, not preferences, and
 * are the reason this is not just a Mantine <Button>:
 *   - the logo is Apple's own glyph, never a substitute or an emoji;
 *   - it keeps a minimum height of 44px and its corner radius stays modest;
 *   - the title reads exactly "Sign in with Apple" (localised by Apple's own
 *     wording, hence the `label` prop rather than a hard-coded string);
 *   - black-on-white or white-on-black only, with no recolouring.
 */

/** Apple's glyph, inlined so the button cannot render logo-less offline. */
function AppleLogo({ size = 18, color = '#fff' }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 170 170"
      aria-hidden="true"
      focusable="false"
      style={{ display: 'block', flex: '0 0 auto' }}
    >
      <path
        fill={color}
        d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.197-2.12-9.973-3.17-14.34-3.17-4.58 0-9.492 1.05-14.746 3.17-5.262 2.13-9.501 3.24-12.742 3.35-4.929 0.21-9.842-1.96-14.746-6.52-3.13-2.73-7.045-7.41-11.735-14.04-5.032-7.08-9.169-15.29-12.41-24.65-3.471-10.11-5.211-19.9-5.211-29.378 0-10.857 2.346-20.221 7.045-28.068 3.693-6.303 8.606-11.275 14.755-14.925s12.793-5.51 19.948-5.629c3.915 0 9.049 1.211 15.429 3.591 6.362 2.388 10.447 3.599 12.238 3.599 1.339 0 5.877-1.416 13.57-4.239 7.275-2.618 13.415-3.702 18.445-3.275 13.63 1.1 23.87 6.473 30.68 16.153-12.19 7.386-18.22 17.731-18.1 31.002 0.11 10.337 3.86 18.939 11.23 25.769 3.34 3.17 7.07 5.62 11.22 7.36-0.9 2.61-1.85 5.11-2.86 7.51zM119.11 7.24c0 8.102-2.96 15.667-8.86 22.669-7.12 8.324-15.732 13.134-25.071 12.375a25.222 25.222 0 0 1-0.188-3.07c0-7.778 3.386-16.102 9.399-22.908 3.002-3.446 6.82-6.311 11.45-8.597 4.62-2.252 8.99-3.497 13.1-3.71 0.12 1.083 0.17 2.166 0.17 3.24z"
      />
    </svg>
  );
}

export default function AppleSignInButton({
  onClick,
  label = 'Sign in with Apple',
  disabled = false,
  width = 280,
}) {
  const [hover, setHover] = React.useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-label={label}
      style={{
        width: width,
        maxWidth: '100%',
        // 44px is Apple's stated minimum and also the platform's minimum touch
        // target, so this is not a number to tune for looks.
        minHeight: 44,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: '0 16px',
        background: '#000',
        color: '#fff',
        border: 'none',
        borderRadius: 6,
        // The Google button next to this one uses the system stack at 14px;
        // matching it is what stops the pair looking mismatched.
        font: '500 14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, '
          + 'Helvetica, Arial, sans-serif',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : (hover ? 0.85 : 1),
        transition: 'opacity 120ms ease',
      }}
    >
      <AppleLogo />
      <span>{label}</span>
    </button>
  );
}
